import React, { useEffect, useMemo, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";

import { managements, defaultConfigData, formRegistry } from "./Data";
import { appConfigList, appConfigUpdate } from "../../api/AppConfiguration";
import SettingLayout from "../settings/SettingLayout";
import { useNavigate, useLocation } from "react-router-dom";

export default function AppConfiguration() {
  const [loading, setLoading] = useState(false);
  const [appConfigs, setAppConfigs] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(() => ({
    title: location.state?.management || "Enquiry",
    tab: location.state?.tab || "enquiry_fields",
  }));

  const [fieldConfig, setFieldConfig] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState("");
  const [workflowConfig, setWorkflowConfig] = useState([]);

  /* ---------------- CURRENT MANAGEMENT ---------------- */

  const currentManagement = useMemo(
    () => managements.find((m) => m.title === filters.title),
    [filters.title],
  );

  /* ---------------- GET SAVED CONFIG ---------------- */

  const getSavedConfig = (management, key) => {
    return appConfigs.find(
      (item) => item.management === management && item.key === key,
    );
  };

  /* ---------------- FETCH APP CONFIG ---------------- */

  const getAppConfig = async () => {
    setLoading(true);

    try {
      const response = await appConfigList();

      if (response?.data?.status) {
        setAppConfigs(response?.data?.data || []);
        localStorage.setItem(
          "app_configs",
          JSON.stringify(response?.data?.data),
        );
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAppConfig();
  }, []);

  /* ---------------- LOAD CONFIG ---------------- */

  useEffect(() => {
    const moduleData =
      defaultConfigData.find((x) => x.title === filters.title) || {};

    const saveData = getSavedConfig(filters.title, filters.tab);

    if (filters.tab.includes("_fields")) {
      const defaultFields = moduleData?.[filters?.tab] || [];

      if (saveData?.value) {
        try {
          const savedFields = JSON.parse(saveData.value);

          const mergedFields = defaultFields.map((field) => {
            const savedField = savedFields.find(
              (item) => item.field === field.field,
            );

            return savedField
              ? {
                  ...field,
                  ...savedField,
                }
              : field;
          });

          setFieldConfig(mergedFields);
        } catch {
          setFieldConfig(defaultFields);
        }
      } else {
        setFieldConfig(defaultFields);
      }
    }
    // if (filters.tab.includes("_fields")) {
    //   if (saveData?.value) {
    //     try {
    //       setFieldConfig(JSON.parse(saveData.value));
    //     } catch {
    //       setFieldConfig(moduleData?.[filters?.tab] || []);
    //     }
    //   } else {
    //     setFieldConfig(moduleData?.[filters?.tab] || []);
    //   }
    // }
    if (filters.tab.includes("_form")) {
      const defaultLayouts = moduleData?.[filters.tab] || [];

      const layoutExists = defaultLayouts.some(
        (layout) => layout.name === saveData?.value,
      );

      setSelectedLayout(
        layoutExists ? saveData.value : defaultLayouts?.[0]?.name || "",
      );
    }

    // if (filters.tab.includes("_form")) {
    //   setSelectedLayout(
    //     saveData?.value || moduleData?.[filters.tab]?.[0]?.name || "",
    //   );
    // }

    if (filters.tab.includes("_workflow")) {
      const defaultWorkflow = moduleData?.[filters?.tab] || [];

      if (saveData?.value) {
        try {
          const savedWorkflow = JSON.parse(saveData.value);

          const mergedWorkflow = defaultWorkflow.map((field) => {
            const savedField = savedWorkflow.find(
              (item) => item.key === field.key,
            );

            return savedField ? { ...field, value: savedField.value } : field;
          });
          setWorkflowConfig(mergedWorkflow);
        } catch {
          setWorkflowConfig(defaultWorkflow);
        }
      } else {
        setWorkflowConfig(defaultWorkflow);
      }
    }

    // if (filters.tab.includes("_workflow")) {
    //   if (saveData?.value) {
    //     try {
    //       setWorkflowConfig(JSON.parse(saveData.value));
    //     } catch {
    //       setWorkflowConfig(moduleData?.[filters?.tab] || []);
    //     }
    //   } else {
    //     setWorkflowConfig(moduleData?.[filters?.tab] || []);
    //   }
    // }
  }, [filters, appConfigs]);

  /* ---------------- FIELD TOGGLE ---------------- */

  const handleFieldToggle = (index, key) => {
    setFieldConfig((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        if (key === "show") {
          const show = item.show ? 0 : 1;

          return {
            ...item,
            show,
            required: show ? item.required : 0,
          };
        }

        if (key === "required") {
          if (!item.show) {
            return item;
          }

          return {
            ...item,
            required: item.required ? 0 : 1,
          };
        }

        return item;
      }),
    );
  };

  /* ---------------- WORKFLOW TOGGLE ---------------- */

  const handleWorkflowChange = (index, value) => {
    setWorkflowConfig((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              value,
            }
          : item,
      ),
    );
  };

  /* ---------------- SAVE UPDATE ---------------- */

  const handleUpdate = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("management", filters.title);
      formData.append("key", filters.tab);
      let saveValue = "";
      if (filters.tab.includes("_fields")) {
        saveValue = JSON.stringify(fieldConfig);
      }
      if (filters.tab.includes("_form")) {
        saveValue = selectedLayout;
      }
      if (filters.tab.includes("_workflow")) {
        saveValue = JSON.stringify(workflowConfig);
      }
      formData.append("value", saveValue);

      const response = await appConfigUpdate(formData);

      if (response?.data?.status) {
        await getAppConfig();
        SweetAlertShow("success", "Success", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const Preview = formRegistry[selectedLayout];

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid container-p-y">
                <div className="row">
                  <SettingLayout />
                  <div className="col-12">
                    <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
                      {managements.map((item) => (
                        <button
                          key={item.title}
                          className={`btn btn-sm ${
                            filters.title === item.title
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() =>
                            setFilters({
                              title: item.title,
                              tab: item.tabs[0].key,
                            })
                          }
                        >
                          <i className={`${item.icon} me-2 ri-20px`}></i>
                          {item.title}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <form onSubmit={handleUpdate}>
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                            <h4 className="text-primary">
                              {filters.title} Configuration
                            </h4>

                            <div className="d-flex gap-2 flex-wrap">
                              {currentManagement?.tabs?.map((tab) => (
                                <button
                                  key={tab.key}
                                  type="button"
                                  onClick={() =>
                                    setFilters((prev) => ({
                                      ...prev,
                                      tab: tab.key,
                                    }))
                                  }
                                  className={`btn ${
                                    filters.tab === tab.key
                                      ? "btn-primary"
                                      : "btn-label-primary"
                                  }`}
                                >
                                  {tab.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-12">
                              {filters.tab.includes("_fields") && (
                                <div className="table-responsive">
                                  <table className="table table-bordered">
                                    <thead>
                                      <tr>
                                        <th>Field</th>
                                        <th>Show</th>
                                        <th>Required</th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {fieldConfig?.map((field, i) => (
                                        <tr key={field.field}>
                                          <td className="py-2">
                                            {field.field
                                              .replaceAll("_", " ")
                                              .toUpperCase()}
                                          </td>

                                          <td className="py-2">
                                            <div className="form-check form-switch mb-0">
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={field.show === 1}
                                                onChange={() =>
                                                  handleFieldToggle(i, "show")
                                                }
                                              />
                                            </div>
                                          </td>

                                          <td className="py-2">
                                            <div className="form-check form-switch mb-0">
                                              <input
                                                className="form-check-input"
                                                type="checkbox"
                                                disabled={!field.show}
                                                checked={field.required === 1}
                                                onChange={() =>
                                                  handleFieldToggle(
                                                    i,
                                                    "required",
                                                  )
                                                }
                                              />
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {filters.tab.includes("_form") && (
                                <div className="row g-3">
                                  {defaultConfigData
                                    .find((x) => x.title === filters.title)
                                    ?.[filters.tab]?.map((form) => (
                                      <div className="col-auto" key={form.name}>
                                        <div
                                          onClick={() =>
                                            setSelectedLayout(form.name)
                                          }
                                          className={`border rounded p-3 ${
                                            selectedLayout === form.name
                                              ? "border-primary border-3"
                                              : "border-secondary"
                                          }`}
                                          style={{
                                            cursor: "pointer",
                                          }}
                                        >
                                          <input
                                            type="radio"
                                            checked={
                                              selectedLayout === form.name
                                            }
                                            readOnly
                                          />

                                          <span className="ms-2 fw-bold">
                                            {form.label}
                                          </span>
                                        </div>
                                      </div>
                                    ))}

                                  <div className="col-md-12">
                                    <div className="card">
                                      <div className="card-header fw-bold">
                                        Live Preview
                                      </div>

                                      <div className="card-body overflow-auto">
                                        {Preview ? (
                                          <Preview />
                                        ) : (
                                          <p>Select form layout for preview</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {filters.tab.includes("_workflow") && (
                                <div className="row">
                                  {workflowConfig
                                    .filter((item) => {
                                      if (!item.show_condition) return true;

                                      const conditionField =
                                        workflowConfig.find(
                                          (f) =>
                                            f.key === item.show_condition.key,
                                        );
                                      if (
                                        item.show_condition.operator ===
                                        "not_null"
                                      ) {
                                        return (
                                          conditionField?.value !== null &&
                                          conditionField?.value !== undefined &&
                                          conditionField?.value !== ""
                                        );
                                      }

                                      if (
                                        item.show_condition.operator === "!="
                                      ) {
                                        return (
                                          conditionField?.value !==
                                          item.show_condition.value
                                        );
                                      }

                                      return (
                                        String(conditionField?.value) ===
                                        String(item.show_condition.value)
                                      );
                                    })
                                    .map((item, i) => (
                                      <div
                                        key={item.key}
                                        className="col-auto mb-3"
                                      >
                                        <div className="d-flex gap-1 align-items-center">
                                          {item.input_type === "select" ? (
                                            <select
                                              className="form-select"
                                              value={item.value}
                                              onChange={(e) =>
                                                handleWorkflowChange(
                                                  workflowConfig.findIndex(
                                                    (f) => f.key === item.key,
                                                  ),
                                                  e.target.value,
                                                )
                                              }
                                            >
                                              {item.options?.map((option) => (
                                                <option
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </option>
                                              ))}
                                            </select>
                                          ) : item.input_type &&
                                            item.input_type !== "checkbox" ? (
                                            <input
                                              type={item.input_type}
                                              className="form-control p-1"
                                              value={item.value}
                                              onChange={(e) =>
                                                handleWorkflowChange(
                                                  workflowConfig.findIndex(
                                                    (f) => f.key === item.key,
                                                  ),
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          ) : (
                                            <div className="form-check form-switch">
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={
                                                  Number(item.value) === 1
                                                }
                                                onChange={(e) =>
                                                  handleWorkflowChange(
                                                    workflowConfig.findIndex(
                                                      (f) => f.key === item.key,
                                                    ),
                                                    e.target.checked ? 1 : 0,
                                                  )
                                                }
                                              />
                                            </div>
                                          )}

                                          <label className="form-label fw-semibold">
                                            {item.label}
                                          </label>

                                          {/* Custom Button */}
                                          {item.button && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={() =>
                                                navigate(item.button.route)
                                              }
                                            >
                                              <i className="ri-settings-3-line me-1"></i>
                                              {item.button.label}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-end mt-4">
                            <button className="btn btn-primary" type="submit">
                              Save Changes
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
